export type StageProfile = 'OPPORTUNITY' | 'PLANNING' | 'RUNNING' | 'HALTED' | 'DONE';
export type StageLike = {id?: string | null; name?: string | null; profile?: StageProfile | string | null} | null | undefined;
type HasStage = {stage?: StageLike} | null | undefined;

export const stageProfileOf = (project: HasStage): StageProfile | null =>
	(project?.stage?.profile as StageProfile | undefined) ?? null;
const STAGE_PROFILES: ReadonlyArray<string> = ['OPPORTUNITY', 'PLANNING', 'RUNNING', 'HALTED', 'DONE'];

/** The list group a project belongs to: its stage's profile, or the stage id for a stage without a profile. */
export const stageGroupKeyOf = (project: HasStage): string | null =>
	project?.stage ? stageProfileOf(project) ?? project.stage.id ?? null : null;

/** Whether a group key (see stageGroupKeyOf) names a stage without a profile rather than a profile. */
export const isCustomStageKey = (key: string | null | undefined): boolean => !!key && !STAGE_PROFILES.includes(key);

export const isProjectLocked = (project: HasStage): boolean => ['DONE', 'HALTED'].includes(stageProfileOf(project) ?? '');
export const isProjectDone = (project: HasStage): boolean => stageProfileOf(project) === 'DONE';
export const isProjectHalted = (project: HasStage): boolean => stageProfileOf(project) === 'HALTED';
export const isProjectOpportunity = (project: HasStage): boolean => stageProfileOf(project) === 'OPPORTUNITY';
export const isProjectPlanning = (project: HasStage): boolean => stageProfileOf(project) === 'PLANNING';
export const isProjectPlanningLike = (project: HasStage): boolean =>
	['PLANNING', 'OPPORTUNITY'].includes(stageProfileOf(project) ?? '');
export const isProjectRunning = (project: HasStage): boolean => stageProfileOf(project) === 'RUNNING';
export const isProjectActive = (project: HasStage): boolean => ['PLANNING', 'RUNNING'].includes(stageProfileOf(project) ?? '');

/** Whether an admin may delete the stage: only stages without a profile, mirroring ProjectStageBehaviour.canDelete. */
export const canDeleteStage = (stage: StageLike): boolean => !!stage && !stage.profile;

/**
 * Whether a list of projects gets an Opportunity group: always with opportunity access, and without it whenever one
 * of the projects is in Opportunity, so a company that lost access still sees those projects.
 */
export const showsOpportunityGroup = (hasOpportunityAccess: boolean, projects: ReadonlyArray<HasStage>): boolean =>
	hasOpportunityAccess || projects.some(isProjectOpportunity);

/** The stage's label: the translated default-stage name when a translation exists, otherwise the name as stored. */
export const stageLabel = (
	stage: StageLike,
	intl: {formatMessage: (d: {id: string}) => string; messages?: Record<string, unknown>}
): string => {
	const name = stage?.name ?? '';
	if (!name) {
		return '';
	}
	const id = `project_status.${name.toLowerCase()}`;
	return intl.messages && Object.prototype.hasOwnProperty.call(intl.messages, id) ? intl.formatMessage({id}) : name;
};

type CustomStageGroup = {key: string; stage: NonNullable<StageLike>};

/**
 * The stages without a profile (custom stages have none for now) the given projects are in, one entry per stage,
 * ordered by the cached sortOrder with stages missing from the cache last. Only stages with at least one project appear.
 */
export const customStageGroupsOf = (
	projects: ReadonlyArray<HasStage>,
	cachedStages: ReadonlyArray<{id: string; sortOrder: number}>
): CustomStageGroup[] => {
	const groups = new Map<string, CustomStageGroup>();
	projects.forEach(project => {
		const key = stageGroupKeyOf(project);
		if (key && isCustomStageKey(key) && !groups.has(key) && project?.stage) {
			groups.set(key, {key, stage: project.stage});
		}
	});
	const sortOrders = new Map(cachedStages.map(stage => [stage.id, stage.sortOrder]));
	const orderOf = (key: string): number => sortOrders.get(key) ?? Infinity;
	return [...groups.values()].sort((a, b) => {
		const [aOrder, bOrder] = [orderOf(a.key), orderOf(b.key)];
		return aOrder === bOrder ? 0 : aOrder < bOrder ? -1 : 1;
	});
};
